/**
 * Aplica o filtro por áreas dos "Últimos focos" ao app inteiro: quando as
 * áreas mudam, baixa os limites (`resolveArea`), entrega os critérios ao store
 * dos focos (que passa a ter só os focos das áreas: mapa, contadores, lista e
 * exportação) e atualiza a camada de destaque no mapa (`areaFocusLayer.ts`).
 *
 * Enquanto os limites de uma área nova baixam, continua valendo o filtro
 * anterior (sem "piscar" todos os focos). Iniciado uma vez por `useFireLayers`.
 */
import type Geometry from 'ol/geom/Geometry';
import { createEmpty, extend, isEmpty, type Extent } from 'ol/extent';
import { useMapInstance } from '../map/mapContext';
import { enquadrarExtensao } from '../map/viewControl';
import { useAreaFilterStore } from './areaFilter';
import type { AreaMatcher } from './areaFires';
import { AreaLoadError, resolveArea, retryAreaLayers } from './areaGeometry';
import { areaId, type AreaRef } from './areaSearch';
import { NO_AREA_FILTER, useFireStore } from './store';

type Listener = (geometries: readonly Geometry[]) => void;

let geometries: readonly Geometry[] = [];
const listeners = new Set<Listener>();
let generation = 0;
/** Enquadrar o mapa nas áreas quando a próxima resolução terminar (pedido do usuário). */
let fitPending = false;

/** Geometrias das áreas aplicadas (para a camada de destaque). */
export function getAreaGeometries(): readonly Geometry[] {
  return geometries;
}

export function onAreaGeometries(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function publish(next: readonly Geometry[]): void {
  geometries = next;
  listeners.forEach((l) => l(next));
}

/** Pede para enquadrar o mapa nas áreas assim que elas estiverem prontas. */
export function requestFitToAreas(): void {
  fitPending = true;
}

/** No celular, a altura da folha dos últimos focos aberta (o enquadramento a desconta). */
function folhaAberta(): number {
  const sheet = typeof document !== 'undefined' ? document.querySelector('[role="dialog"][data-testid="recent-fires"]') : null;
  return sheet ? sheet.getBoundingClientRect().height : 0;
}

function fitToAreas(list: readonly Geometry[]): void {
  const map = useMapInstance.getState().map;
  if (!map || !list.length) return;
  const extent: Extent = createEmpty();
  for (const g of list) extend(extent, g.getExtent());
  if (isEmpty(extent)) return;
  // Até o zoom máximo do satélite: com o limite antigo (1,5× a resolução nativa) Santarém ficava
  // pequena no meio do mapa e não se destacava (08/10/2026).
  enquadrarExtensao(map, extent, { embaixo: folhaAberta() });
}

function apply(areas: readonly AreaRef[]): void {
  const gen = ++generation;
  const store = useFireStore.getState();
  if (!areas.length) {
    fitPending = false;
    store.setAreaFilter(null, NO_AREA_FILTER);
    publish([]);
    return;
  }
  // Mantém os critérios anteriores até os novos ficarem prontos.
  store.setAreaFilter(undefined, { state: 'loading', missing: store.areaStatus.missing });
  void Promise.allSettled(areas.map((a) => resolveArea(a))).then((results) => {
    if (gen !== generation) return;
    const matchers: AreaMatcher[] = [];
    const geoms: Geometry[] = [];
    const missing: string[] = [];
    let failed = false;
    results.forEach((r, k) => {
      if (r.status === 'fulfilled') {
        matchers.push(r.value.matcher);
        geoms.push(...r.value.geometries);
      } else if (r.reason instanceof AreaLoadError) failed = true;
      else missing.push(areaId(areas[k]!));
    });
    if (failed) {
      // Sem os limites de alguma área, não dá para filtrar com segurança: mostra tudo e avisa.
      fitPending = false;
      useFireStore.getState().setAreaFilter(null, { state: 'error', missing });
      publish([]);
      return;
    }
    useFireStore.getState().setAreaFilter(matchers, { state: 'ready', missing });
    publish(geoms);
    if (fitPending) {
      fitPending = false;
      fitToAreas(geoms);
    }
  });
}

/** "Tentar de novo" depois de falha ao baixar os limites. */
export function retryAreaFilter(): void {
  const { areas } = useAreaFilterStore.getState();
  retryAreaLayers(areas);
  apply(areas);
}

let refCount = 0;
let unsubscribe: (() => void) | null = null;

export function startAreaSync(): () => void {
  refCount++;
  if (refCount === 1) {
    unsubscribe = useAreaFilterStore.subscribe((s, prev) => {
      if (s.areas.map(areaId).join('|') !== prev.areas.map(areaId).join('|')) apply(s.areas);
    });
    apply(useAreaFilterStore.getState().areas);
  }
  let stopped = false;
  return () => {
    if (stopped) return;
    stopped = true;
    refCount--;
    if (refCount === 0) {
      unsubscribe?.();
      unsubscribe = null;
    }
  };
}
